import type { ComponentChildren } from 'preact';
import { useTranslation } from '../i18n';

/**
 * Shared bottom-of-page footnote strip. Bordered top, mono uppercase, with a left, middle
 * (legal links), and right cell that flex-wrap on narrow screens.
 *
 * In the unified SPA, Privacy + Terms are served from the same origin under `/privacy` and
 * `/tos` (no www-subdomain crossing). Set `showLegalLinks={false}` to suppress.
 */
export function PlatformFootnote({
  left,
  right,
  cap = 'wide',
  embedded = false,
  showLegalLinks = true,
}: {
  left: ComponentChildren;
  right: ComponentChildren;
  cap?: 'wide' | 'screen-2xl';
  embedded?: boolean;
  showLegalLinks?: boolean;
}) {
  const maxWidth = cap === 'wide' ? 'max-w-[1680px]' : 'max-w-screen-2xl';
  const gutter = cap === 'wide' ? 'px-[clamp(20px,3.5vw,56px)]' : 'px-[clamp(20px,4vw,56px)]';

  const legal = showLegalLinks ? <LegalLinks /> : null;

  if (embedded) {
    return (
      <div class="flex flex-wrap items-center justify-between gap-3 border-t border-rule pt-6 font-mono text-[11px] tracking-[0.04em] text-fg-faint">
        <span>{left}</span>
        {legal && <span class="order-3 w-full text-center sm:order-2 sm:w-auto">{legal}</span>}
        <span class="order-2 sm:order-3">{right}</span>
      </div>
    );
  }

  return (
    <footer class="border-t border-rule font-mono text-[11px] tracking-[0.04em] text-fg-faint">
      <div class={`mx-auto flex ${maxWidth} flex-wrap items-center justify-between gap-3 ${gutter} py-6`}>
        <span>{left}</span>
        {legal && <span class="order-3 w-full text-center sm:order-2 sm:w-auto">{legal}</span>}
        <span class="order-2 sm:order-3">{right}</span>
      </div>
    </footer>
  );
}

function LegalLinks() {
  const t = useTranslation();
  return (
    <span class="inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
      <a href="/privacy" class="hover:text-fg-primary">{t('footer.privacy')}</a>
      <span aria-hidden="true">·</span>
      <a href="/tos" class="hover:text-fg-primary">{t('footer.terms')}</a>
    </span>
  );
}
